import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { showInkToast } from '../cms/InkToast';
import {
  BillingContact,
  SET_BILLING_EMAIL,
  billingEmailLine,
  billingWarnings,
  ownerOnlyNote,
} from './siteOwner';

/**
 * Plan & billing → Owner & billing email: who manages billing, where Stripe
 * sends receipts and payment problems, and what needs fixing when billing
 * still points at someone who left. Only the owner changes the email.
 */
export function BillingContactSection({
  accountId,
  contact,
  onChanged,
}: {
  accountId: string;
  contact: BillingContact | null;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [save, { loading: saving }] = useMutation(SET_BILLING_EMAIL);

  if (!contact) return null;
  const { email, why } = billingEmailLine(contact);
  const warnings = billingWarnings(contact);
  const note = ownerOnlyNote(contact);

  const submit = async (value: string | null) => {
    setError('');
    try {
      await save({ variables: { accountId, email: value } });
      setEditing(false);
      onChanged();
      showInkToast(
        value ? `Billing emails go to ${value} now.` : 'Billing emails go to the owner now.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    }
  };

  return (
    <>
      <h2 className="ink-bill-h">Owner &amp; billing email</h2>
      {/* One table, a label on the left of each row (Prototype). */}
      <div className="ink-bill-owner">
        <div className="ink-bill-owner-row">
          <span className="ink-bill-owner-label">Owner</span>
          <span className="ink-bill-owner-value">
            <strong>{contact.ownerEmail || 'No owner yet: any admin can manage billing'}</strong>
            {contact.isOwner ? <span className="ink-bill-who-you"> · you</span> : null}
          </span>
          {contact.canManage ? (
            <Link to="/settings/team" className="ink-bill-link">
              Hand over in Team
            </Link>
          ) : (
            <span />
          )}
        </div>
        <div className="ink-bill-owner-row" data-editing={editing ? 'true' : undefined}>
          {editing ? (
            <>
              <label className="ink-bill-owner-label" htmlFor="billing-email">
                Billing email
              </label>
              <form
                className="ink-bill-email-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submit(draft.trim() || null);
                }}
              >
                <input
                  id="billing-email"
                  className="ink-input"
                  type="email"
                  autoComplete="email"
                  placeholder="Billing email"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  autoFocus
                />
                <button type="submit" className="ink-btn ink-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="ink-bill-link"
                  data-muted="true"
                  onClick={() => {
                    setEditing(false);
                    setError('');
                  }}
                >
                  Cancel
                </button>
              </form>
              <span />
            </>
          ) : (
            <>
              <span className="ink-bill-owner-label">Billing email</span>
              <span className="ink-bill-owner-value">
                <strong>{email}</strong>
                {why ? <span className="ink-bill-who-you"> · {why}</span> : null}
              </span>
              {contact.canManage ? (
                <span className="ink-bill-owner-actions">
                  {contact.billingEmail ? (
                    <button
                      type="button"
                      className="ink-bill-link"
                      data-muted="true"
                      disabled={saving}
                      onClick={() => void submit(null)}
                    >
                      Use the owner’s email
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="ink-bill-link"
                    onClick={() => {
                      setDraft(contact.billingEmail || '');
                      setEditing(true);
                    }}
                  >
                    Change
                  </button>
                </span>
              ) : (
                <span />
              )}
            </>
          )}
        </div>
      </div>
      {error ? <p className="cp-error">{error}</p> : null}
      <p className="ink-bill-sub" style={{ marginTop: 8 }}>
        Receipts, renewals and payment problems go here. A shared inbox, like
        billing@yourcompany.com, keeps working when people change roles.
      </p>
      {warnings.map((w) => (
        <p className="ink-bill-note" data-tone="warn" key={w}>
          {w}
        </p>
      ))}
      {note ? <p className="ink-bill-note">{note}</p> : null}
    </>
  );
}
